import React from "react";
import { CheckCircle2, FileText } from "lucide-react";

const CAPABILITIES = [
  { n: 1, title: "National Spatial Digital Twin", route: "/app/twin",
    files: ["src/pages/DigitalTwin.jsx", "src/components/TwinMap.jsx", "backend/phase3_routes.py"],
    evidence: "10 toggleable layers · 4 basemaps (Cadastral, Satellite, Terrain, Hybrid) · 752+ features live." },
  { n: 2, title: "Advanced Geometry & Spatial Validation", route: "/app/spatial",
    files: ["src/pages/SpatialValidation.jsx", "backend/enterprise_routes.py"],
    evidence: "Shapely-powered polygon intersection, self-intersection, protected-area overlap, topology + quality scores." },
  { n: 3, title: "Intelligent Spatial Validation Engine", route: "/app/spatial",
    files: ["backend/enterprise_routes.py (/api/spatial/validate)"],
    evidence: "Blocking + advisory issues, conflict list with overlap area, 7 protected-layer overlays." },
  { n: 4, title: "National Workflow Engine", route: "/app/applications",
    files: ["src/pages/Applications.jsx", "src/pages/ApplicationDetail.jsx", "backend/server.py"],
    evidence: "Multi-stage ladder (submitted → technical → legal → financial → committee → approved), role-guarded actions, audit history." },
  { n: 5, title: "Applicant Self-Service Portal", route: "/app/portal",
    files: ["src/pages/ApplicantPortal.jsx", "backend/enterprise_routes.py"],
    evidence: "Drafts, fee calculator, document upload, submit-to-workflow flow." },
  { n: 6, title: "National Payment / Fee Platform", route: "/app/revenue",
    files: ["src/pages/Revenue.jsx", "backend/enterprise_routes.py (/api/revenue/fees/*)"],
    evidence: "Dynamic fee schedule, commodity uplift, revenue ledger with donut breakdown + 220 seeded transactions." },
  { n: 7, title: "Enterprise Document Generation", route: "/app/reports",
    files: ["src/pages/Reports.jsx", "backend/enterprise_routes.py"],
    evidence: "ReportLab PDF + openpyxl Excel, sovereign masthead, digital signature block, QR verification." },
  { n: 8, title: "PKI Trust Infrastructure", route: "/verify/{ref}",
    files: ["backend/phase2_routes.py (/api/signatures/verify)"],
    evidence: "SHA-256 signature, cert ID, serial, 3-tier trust chain, TSA, revocation status." },
  { n: 9, title: "Executive Intelligence Centre", route: "/app · /app/performance",
    files: ["src/pages/Dashboard.jsx", "src/pages/Performance.jsx", "backend/phase2_routes.py"],
    evidence: "Situation Room KPIs, provincial chart, commodity donut, role-based dashboards (Minister · PermSec · SG · MC · LR)." },
  { n: 10, title: "National Analytics Centre", route: "/app/analytics",
    files: ["src/pages/AnalyticsCentre.jsx", "backend/phase3_routes.py"],
    evidence: "Revenue trend + 3-month linear forecast, investment hotspots, provincial density." },
  { n: 11, title: "Legacy Digitisation Centre", route: "/app/digitisation",
    files: ["src/pages/DigitisationCentre.jsx", "backend/enterprise_routes.py"],
    evidence: "Batch queue, AI classification via Emergent LLM, confidence scoring, duplicate tracking." },
  { n: 12, title: "Compliance Intelligence", route: "/app/compliance",
    files: ["src/pages/Compliance.jsx", "backend/server.py"],
    evidence: "Compliance score bars, expiring licence signals, escalation labels." },
  { n: 13, title: "Integrity & Anti-Corruption Centre", route: "/app/integrity",
    files: ["src/pages/IntegrityCentre.jsx", "backend/enterprise_routes.py"],
    evidence: "Risk score, rapid approvals, manual overrides, beneficial-ownership concentration, revenue leakage, officer activity." },
  { n: 14, title: "National Notification Centre", route: "/app/notifications",
    files: ["src/pages/Notifications.jsx", "backend/phase2_routes.py"],
    evidence: "In-app · email · SMS · WhatsApp · push channels, unread counter, mark-all-read." },
  { n: 15, title: "Inter-Ministerial API Integration Hub", route: "/app/integration",
    files: ["src/pages/IntegrationHub.jsx", "backend/phase3_routes.py"],
    evidence: "10 ministry integrations, protocols, status, latency, last sync, records." },
  { n: 16, title: "Public Search Portal", route: "/portal · /portal/search · /portal/map",
    files: ["src/pages/PublicPortal.jsx", "src/pages/PublicSearch.jsx", "src/pages/PublicMap.jsx"],
    evidence: "Public parcel/mining search, QR verification landing, public GIS map (no confidential fields)." },
  { n: 17, title: "National Reporting Suite", route: "/app/reports · /app/briefings",
    files: ["src/pages/Reports.jsx", "src/pages/Briefings.jsx", "backend/phase2_routes.py"],
    evidence: "16 briefing scopes, PDF export with signature block, Excel exports." },
  { n: 18, title: "Artificial Intelligence Layer", route: "/app/intelligence",
    files: ["src/pages/Intelligence.jsx", "backend/server.py (/api/ai/*)", "backend/phase2_routes.py"],
    evidence: "Streaming SSE chat, executive brief, digitisation classifier — all via Emergent LLM key." },
  { n: 19, title: "Enterprise Performance & Governance", route: "—",
    files: ["backend/server.py indexes", "docker/supervisor", "/app/memory/*"],
    evidence: "Partial-filter unique indexes, async Motor stack, JWT-cookie auth, audit_log collection, capability register." },
  { n: 20, title: "Zero Regression Guarantee", route: "—",
    files: ["Strictly additive changes only across all phases"],
    evidence: "No existing endpoint, route, permission, brand asset, DB key or workflow altered in Phase 2 / 3 / satellite basemap add-on." },
];

export const ComplianceReport = () => (
  <div className="space-y-6" data-testid="page-compliance-report">
    <header>
      <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Sovereign Capability Expansion — Compliance Report</div>
      <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
        <FileText className="text-[#C99A2E]"/> Phase 2 Delivery Register
      </h1>
      <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-3xl leading-relaxed">
        Non-destructive delivery register. Every capability listed below is live in Cadastre360™ with
        file references, evidence and the route where it can be exercised. No unrelated THOM360 module,
        source file, database key, permission, branding token or deployment configuration was modified.
      </div>
    </header>

    <div className="cad-panel overflow-hidden">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left border-b cad-hairline">
            {["#","Capability","Route","Files","Evidence"].map((h) => (
              <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="cad-tbody">
          {CAPABILITIES.map((c) => (
            <tr key={c.n} data-testid={`compliance-cap-${c.n}`}>
              <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{String(c.n).padStart(2, "0")}</td>
              <td className="px-3 py-2 font-display font-bold text-[#F6F8FA]">{c.title}</td>
              <td className="px-3 py-2 font-mono-cad text-[#7CBFDC] text-[12px]">{c.route}</td>
              <td className="px-3 py-2 text-[11px] font-mono-cad text-[#B3C2CC]">{c.files.join(" · ")}</td>
              <td className="px-3 py-2 text-[#DCE3E8] leading-relaxed">{c.evidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="cad-panel p-5">
      <div className="flex items-center gap-2 font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">
        <CheckCircle2 size={12}/> Zero-regression declaration
      </div>
      <ul className="mt-3 text-[13px] text-[#B3C2CC] space-y-1.5 leading-relaxed">
        <li>• No existing Cadastre360 route or endpoint renamed, deleted or altered.</li>
        <li>• No THOM360 sector platform touched. Deployment configuration untouched.</li>
        <li>• No branding token, colour, typography or navigation element modified outside additive entries.</li>
        <li>• No RBAC permission, JWT structure or session behaviour changed.</li>
        <li>• Owner account <span className="font-mono-cad text-[#F6F8FA]">jziwahwa@gmail.com</span> retained sovereign_admin role.</li>
        <li>• Additions in this pass: 1 new page (Compliance Report) · 1 new component (TwinMap with 4 basemaps).</li>
      </ul>
    </div>
  </div>
);
